/* i18n-scan-ignore-file: reproduce el documento del carrier, no es interfaz */
/**
 * Plantilla imprimible de la tarjeta de identificación.
 *
 * **Su texto no se traduce**, por lo mismo que `idCardData`: los títulos
 * (`Marketplace ID Card`, `Member ID Card`…) son los del documento que emite
 * cada aseguradora.
 */
import {PolicyCardData} from './idCardData'

type CarrierTemplate = 'molina' | 'uhc' | 'ambetter' | 'oscar' | 'sentara' | 'generic'

const escapeHtml = (value?: string | null) => {
  const raw = String(value || '')
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

const hasDisplayValue = (value?: string | null) => {
  const text = String(value || '').trim()
  return text.length > 0 && text.toUpperCase() !== 'N/A' && text !== 'N/A / N/A'
}

const detectCarrierTemplate = (carrierName?: string | null): CarrierTemplate => {
  const name = (carrierName || '').toLowerCase()
  if (name.includes('molina')) return 'molina'
  if (name.includes('unitedhealthcare') || name.includes('uhc') || name.includes('optum')) return 'uhc'
  if (name.includes('ambetter') || name.includes('superior healthplan')) return 'ambetter'
  if (name.includes('oscar')) return 'oscar'
  if (name.includes('sentara')) return 'sentara'
  return 'generic'
}

const getTemplateColors = (template: CarrierTemplate) => {
  switch (template) {
    case 'molina':
      return {primary: '#00a9b5', accent: '#6a2a66', text: '#101820'}
    case 'uhc':
      return {primary: '#0057b8', accent: '#f28c00', text: '#101820'}
    case 'ambetter':
      return {primary: '#cf1e8d', accent: '#30343b', text: '#1f252d'}
    case 'oscar':
      return {primary: '#5b5ce6', accent: '#111111', text: '#111111'}
    case 'sentara':
      return {primary: '#c7cced', accent: '#9ea6d2', text: '#111111'}
    default:
      return {primary: '#1763a6', accent: '#222222', text: '#121212'}
  }
}

const getTemplateTitle = (template: CarrierTemplate, data: PolicyCardData) => {
  switch (template) {
    case 'molina':
      return 'Marketplace ID Card'
    case 'uhc':
      return 'Current ID Card'
    case 'ambetter':
      return 'Member ID Card'
    case 'oscar':
      return 'Plan ID Card'
    case 'sentara':
      return 'Digital ID Card'
    default:
      return `${data.carrierName} Member Card`
  }
}

const buildSentaraBackHtml = (data: PolicyCardData) => {
  return `
    <div class="back-sentara">
      <div class="sn-note">For emergency care, dial 911 or go to the nearest urgent/emergency facility.</div>
      <div class="sn-rule"></div>
      <div class="sn-services">
        <div><b>Member Services:</b><span>${escapeHtml(data.memberServicesPhone)}</span></div>
        <div class="sn-sub">(Hearing impaired / Virginia Relay: ${escapeHtml(data.issuerTty)})</div>
        <div><b>Provider Services:</b><span>${escapeHtml(data.providerPhone)}</span></div>
        <div class="sn-sub">(Including Pre-Authorization)</div>
        <div><b>24/7 Nurse Advice Line:</b><span>${escapeHtml(data.nurseLine)}</span></div>
        <div><b>Pharmacist Help Desk:</b><span>${escapeHtml(data.pharmacyPhone)}</span></div>
        <div><b>Behavioral Health Crisis:</b><span>${escapeHtml(data.mentalHealthPhone)}</span></div>
      </div>
      <div class="sn-rule"></div>
      <div class="sn-claims-title">Submit paper claims to:</div>
      <div class="sn-claims-grid">
        <div>
          <b>MEDICAL CLAIMS</b>
          <div>${escapeHtml(data.medicalClaimsAddress)}</div>
        </div>
        <div>
          <b>BEHAVIORAL HEALTH CLAIMS</b>
          <div>${escapeHtml(data.claimsMentalHealth)}</div>
        </div>
      </div>
      <div class="sn-footer">An HMO plan underwritten by Sentara Health Plans</div>
    </div>
  `
}

const buildOscarBackHtml = (data: PolicyCardData) => {
  const doctorsRows: Array<[string, string, string?, string?]> = []
  if (hasDisplayValue(data.rxBin) || hasDisplayValue(data.payerId)) {
    doctorsRows.push(['RxBIN', data.rxBin, 'Payer ID', data.payerId])
  }
  if (hasDisplayValue(data.rxPcn) || hasDisplayValue(data.planType)) {
    doctorsRows.push(['RxPCN', data.rxPcn, 'Plan type', data.planType])
  }
  if (hasDisplayValue(data.rxGrp)) {
    doctorsRows.push(['RxGRP', data.rxGrp])
  }

  const claimRows: Array<[string, string]> = []
  if (hasDisplayValue(data.claimsMentalHealth)) claimRows.push(['Mental health', data.claimsMentalHealth])
  if (hasDisplayValue(data.claimsPharmacy)) claimRows.push(['Pharmacy', data.claimsPharmacy])
  if (hasDisplayValue(data.claimsPediatricVision)) claimRows.push(['Pediatric vision', data.claimsPediatricVision])
  if (hasDisplayValue(data.claimsMedical)) claimRows.push(['Medical', data.claimsMedical])

  return `
    <div class="back-oscar">
      <div class="os-title">Your Care Team</div>
      <div class="os-text">Log in at ${escapeHtml(data.website)}, or call ${escapeHtml(data.careTeamPhone)}</div>

      <div class="os-title">Mental health</div>
      <div class="os-text">Call Optum at ${escapeHtml(data.mentalHealthPhone)}</div>

      <div class="os-title">For your doctors &amp; pharmacy</div>
      <div class="os-grid4">
        ${doctorsRows
          .map((row) => {
            const leftLabel = row[0]
            const leftValue = row[1]
            const rightLabel = row[2] || ''
            const rightValue = row[3] || ''
            return `<div>${escapeHtml(leftLabel)}</div><div>${escapeHtml(leftValue)}</div><div>${escapeHtml(
              rightLabel
            )}</div><div>${escapeHtml(rightValue)}</div>`
          })
          .join('')}
      </div>
      <div class="os-grid2 os-calls">
        <div>Providers call</div><div>${escapeHtml(data.providerPhone)}</div>
        <div>Pharmacists call</div><div>${escapeHtml(data.pharmacyPhone)}</div>
      </div>

      <div class="os-title">Where to send claims</div>
      <div class="os-grid2">
        ${claimRows.map((row) => `<div>${escapeHtml(row[0])}</div><div>${escapeHtml(row[1])}</div>`).join('')}
      </div>
    </div>
  `
}

const buildCardHtml = (data: PolicyCardData) => {
  const template = detectCarrierTemplate(data.carrierName)
  const colors = getTemplateColors(template)
  const title = getTemplateTitle(template, data)
  const frontRows = [
    ['Plan', data.planName],
    ['Subscriber', data.subscriberName],
    ['Member ID', data.memberId],
    ['Policy #', data.policyId],
    ['Effective', data.effectiveDate],
    ['Type / Metal', `${data.planType} / ${data.metalLevel}`],
    ['Deductible', data.medicalDeductible],
    ['OOPM', data.annualOopm],
  ].filter(([, value]) => hasDisplayValue(value))
  const genericBackRows = [
    ['Member Services', data.memberServicesPhone],
    ['Provider', data.providerPhone],
    ['Pharmacy', data.pharmacyPhone],
    ['Nurse Line', data.nurseLine],
    ['Billing', data.billingPhone],
    ['Provider Help Desk', data.providerHelpDesk],
    ['Prior Auth', data.priorAuthPhone],
    ['Care Team', data.careTeamPhone],
    ['Mental Health', data.mentalHealthPhone],
    ['TTY', data.issuerTty],
    ['Medical Claims', data.medicalClaimsAddress],
    ['Pharmacy Claims', data.pharmacyClaimsAddress],
    ['Payer ID', data.payerId],
    ['Portal', data.providerPortal],
    ['Website', data.website],
    ['Rx BIN / PCN / GRP', `${data.rxBin} / ${data.rxPcn} / ${data.rxGrp}`],
    ['Referral', data.referralRequired],
  ].filter(([, value]) => hasDisplayValue(value))
  const genericSmallRows = [
    ['Benefits', data.benefitsUrl],
    ['Formulary', data.formularyUrl],
    ['Network', data.providerNetworkUrl],
    ...data.additionalBackLines.map((line, index) => [`Note ${index + 1}`, line] as [string, string]),
  ].filter(([, value]) => hasDisplayValue(value))

  return `
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(data.memberName)} - ID Card</title>
        <style>
          @page {
            size: A4;
            margin: 8mm;
          }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            color: ${colors.text};
            font-family: Arial, Helvetica, sans-serif;
            background: #ffffff;
          }
          .sheet {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8mm;
          }
          .cut-zone {
            width: 93.6mm;
            height: 61.98mm;
            border: 0.3mm dashed #8f8f8f;
            padding: 4mm;
            position: relative;
          }
          .side-label {
            position: absolute;
            top: 1.2mm;
            right: 2mm;
            font-size: 8px;
            color: #6f6f6f;
            letter-spacing: 0.6px;
          }
          .card {
            width: 85.6mm;
            height: 53.98mm;
            border: 0.35mm solid #d4d4d4;
            border-radius: 3.2mm;
            overflow: hidden;
            background: #fff;
          }
          .head {
            background: linear-gradient(120deg, ${colors.primary}, ${colors.accent});
            color: #fff;
            padding: 1.4mm 2.2mm;
            display: flex;
            justify-content: space-between;
            align-items: center;
            min-height: 8.2mm;
          }
          .brand {
            font-weight: 700;
            font-size: 7.8px;
            line-height: 1.0;
            max-width: 53mm;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .title {
            font-size: 6px;
            font-weight: 700;
            text-align: right;
            max-width: 27mm;
            line-height: 1.0;
            max-height: 6.2mm;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
          .content {
            padding: 2mm 2.6mm;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.2mm 2mm;
            font-size: 8px;
            line-height: 1.0;
          }
          .field {
            display: flex;
            flex-direction: column;
            min-width: 0;
          }
          .field b {
            font-size: 6.8px;
            text-transform: uppercase;
            color: #4a4a4a;
            letter-spacing: 0.2px;
          }
          .field span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .full { grid-column: 1 / -1; }
          .stripe {
            margin-top: 0.4mm;
            border-top: 0.35mm solid ${colors.primary};
            padding-top: 0.9mm;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.7mm 1.6mm;
            font-size: 7.2px;
            line-height: 1.0;
          }
          .note {
            margin-top: 1.2mm;
            font-size: 7.6px;
            color: #4f4f4f;
          }
          .footer {
            margin-top: 1.8mm;
            font-size: 7.6px;
            color: #606060;
            text-align: center;
          }
          .footer-oscar {
            margin-top: 0.35mm;
            padding: 0 2mm 0.9mm;
            font-size: 6px;
            line-height: 1.05;
            color: #242424;
            text-align: left;
          }
          .back-content {
            padding: 2.6mm 3mm;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.2mm 2.2mm;
            font-size: 9px;
            line-height: 1.05;
          }
          .small {
            font-size: 8px;
            color: #555;
          }
          .back-oscar { padding: 1.1mm 2mm; font-size: 7px; line-height: 0.98; }
          .os-title {
            font-size: 6.6px;
            font-weight: 700;
            padding-bottom: 0.3mm;
            margin-bottom: 0.35mm;
            border-bottom: 0.25mm solid #1b1b1b;
          }
          .os-text { margin-bottom: 0.35mm; }
          .os-grid4 {
            display: grid;
            grid-template-columns: 1.1fr 1fr 1.1fr 1fr;
            gap: 0.15mm 0.55mm;
            margin-bottom: 0.3mm;
          }
          .os-grid2 {
            display: grid;
            grid-template-columns: 1.2fr 1fr;
            gap: 0.15mm 0.55mm;
            margin-bottom: 0.3mm;
          }
          .os-calls { padding-top: 0.25mm; border-top: 0.2mm solid #1f1f1f; }
          .os-address { margin-top: 0.05mm; }
          .card-oscar .head {
            padding: 1.1mm 1.8mm;
            min-height: 7.2mm;
          }
          .card-oscar .brand {
            font-size: 7px;
            max-width: 52mm;
          }
          .card-oscar .title {
            font-size: 5.4px;
            max-width: 26mm;
            max-height: 5.5mm;
          }
          .card-sentara .head {
            background: linear-gradient(180deg, #c7cced, #b1b8df);
            min-height: 7.6mm;
            padding: 1.2mm 2mm;
          }
          .card-sentara .brand {
            font-size: 7.2px;
            max-width: 52mm;
            color: #111;
          }
          .card-sentara .title {
            font-size: 5.8px;
            color: #111;
          }
          .back-sentara {
            padding: 1.7mm 2.3mm 1.2mm;
            font-size: 6.4px;
            line-height: 1.08;
          }
          .sn-note {
            font-size: 5.9px;
            margin-bottom: 0.6mm;
          }
          .sn-rule {
            border-top: 0.2mm solid #8c8c8c;
            margin: 0.55mm 0;
          }
          .sn-services {
            display: grid;
            grid-template-columns: 1fr;
            gap: 0.35mm;
          }
          .sn-services > div {
            display: flex;
            justify-content: space-between;
            gap: 1mm;
          }
          .sn-services b {
            font-weight: 700;
          }
          .sn-sub {
            font-size: 5.4px;
            color: #555;
            justify-content: flex-start !important;
          }
          .sn-claims-title {
            text-align: center;
            font-weight: 700;
            margin-bottom: 0.55mm;
          }
          .sn-claims-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.2mm;
            font-size: 5.8px;
          }
          .sn-claims-grid b {
            display: block;
            margin-bottom: 0.3mm;
          }
          .sn-footer {
            margin-top: 0.7mm;
            text-align: center;
            font-size: 5.8px;
          }
          @media print {
            .cut-zone { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="cut-zone">
            <div class="side-label">FRONT</div>
            <div class="card card-${template}">
              <div class="head">
                <div class="brand">${escapeHtml(data.carrierName)}</div>
                <div class="title">${escapeHtml(title)}</div>
              </div>
              <div class="content">
                ${frontRows
                  .map(
                    ([label, value], index) =>
                      `<div class="field ${index === 0 ? 'full' : ''}"><b>${escapeHtml(label)}</b><span>${escapeHtml(
                        value
                      )}</span></div>`
                  )
                  .join('')}
              </div>
              <div class="content" style="padding-top: 0;">
                <div class="stripe full">
                  ${
                    [
                      ['PCP', data.pcp],
                      ['Specialist', data.specialist],
                      ['Urgent', data.urgentCare],
                      ['ER', data.emergencyRoom],
                      ['Rx Generic', data.genericRx],
                      ['Rx Brand', data.preferredBrandRx],
                    ]
                      .filter(([, value]) => hasDisplayValue(value))
                      .map(([label, value]) => `<div><b>${escapeHtml(label)}:</b> ${escapeHtml(value)}</div>`)
                      .join('')
                  }
                </div>
              </div>
            </div>
          </div>

          <div class="cut-zone">
            <div class="side-label">BACK</div>
            <div class="card card-${template}">
              <div class="head">
                <div class="brand">Member / Provider Support</div>
                <div class="title">${escapeHtml(data.carrierName)}</div>
              </div>
              ${
                template === 'oscar'
                  ? buildOscarBackHtml(data)
                  : template === 'sentara'
                    ? buildSentaraBackHtml(data)
                    : `<div class="back-content">
                ${genericBackRows
                  .map(([label, value]) => {
                    const full = label.includes('Claims') || label === 'Website'
                    return `<div class="${full ? 'full' : ''}"><b>${escapeHtml(label)}:</b><br/>${escapeHtml(value)}</div>`
                  })
                  .join('')}
                ${genericSmallRows
                  .map(([label, value]) => `<div class="full small"><b>${escapeHtml(label)}:</b> ${escapeHtml(value)}</div>`)
                  .join('')}
              </div>`
              }
              ${
                template === 'oscar'
                  ? `<div class="footer-oscar">
                       <div>${escapeHtml(data.medicalClaimsAddress)}</div>
                       <div>Tell your provider you're with QualCare through Oscar.</div>
                       <div>Insured by Oscar Garden State Insurance Corporation</div>
                     </div>`
                  : template === 'sentara'
                    ? ''
                  : '<div class="footer">This card is for identification purposes only.</div>'
              }
            </div>
          </div>
        </div>
      </body>
    </html>
  `
}

export const openPrintableMemberIdCard = (cardData: PolicyCardData) => {
  const printWindow = window.open('', '_blank', 'width=1024,height=900')
  if (!printWindow) return false
  const html = buildCardHtml(cardData)
  printWindow.document.open()
  printWindow.document.write(html)
  printWindow.document.close()
  printWindow.focus()
  setTimeout(() => {
    printWindow.print()
  }, 250)
  return true
}
